import { useMemo } from "react";
import { useOrganization } from "../context/OrganizationContext";
import { Deal } from "../models/shared/Deal";
import { COLLECTIONS } from "../utils/shared/constants";
import { useFirebaseQuery } from "./firebaseHooks";
import { limit, QueryConstraint } from "firebase/firestore";
import { UserRole } from "../models/shared/UserInvitation";
import { useUser } from "../context/UserContext";

export interface DealFilters {
    steps?: string[];
    tags?: string[];
}

interface Options {
    limit?: number;
    filters?: DealFilters;
}

export const useDeals = (options: Options = {}) => {
    const { user } = useUser();
    const { orgId, orgRole } = useOrganization();
    const constrains: QueryConstraint[] = [];
    if (options.limit) {
        constrains.push(limit(options.limit));
    }
    const [allUnsortedDeals] = useFirebaseQuery<Deal>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}`, ...constrains);

    const dealsPerId = useMemo(() => {
        const dealsPerId: Record<string, Deal> = {};

        for (const deal of allUnsortedDeals) {
            dealsPerId[deal.id || ''] = deal;
        }
        return dealsPerId;
    }, [allUnsortedDeals])

    const allDeals = useMemo(() => {
        let allSortedDeals = [...allUnsortedDeals].filter(deal => deal.status !== 'archived');

        if (options.filters?.steps && options.filters.steps.length > 0) {
            allSortedDeals = allSortedDeals.filter(deal => options.filters?.steps?.includes(deal.stepId || ''));
        }

        if (options.filters?.tags && options.filters.tags.length > 0) {
            allSortedDeals = allSortedDeals.filter(deal => deal.tags?.some(tag => options.filters?.tags?.includes(tag)));
        }

        if (orgRole === UserRole.WORKER) {
            allSortedDeals = allSortedDeals.filter(deal => {
                const assignedTo = deal.assignedTo || [];
                const userEmail = user?.email || '';
                return assignedTo.includes(userEmail);
            });
        }


        allSortedDeals.sort((a, b) => (a.order || 0) - (b.order || 0));
        return allSortedDeals;
    }, [allUnsortedDeals, options.filters, orgRole, user?.email]);

    return { dealsPerId, allDeals };
};